import { Box } from "@mui/material";
import { componentID } from "core/services/componentService/component-service-base";
import componentService from "core/services/componentService/component-service";
import React, { useEffect, useRef } from "react";
import baseSimpleSourceModule from "app/components/appwizard/component-wizards/wizards/source/base-simple-source/base-simple-source";
// @ts-ignore
import getControlResource from "app/components/common/editor/control-resource-resolver";
// @ts-ignore
import metaStoreService from "core/services/metaStoreService/meta-store-service";
// @ts-ignore
import { StriimButton } from "@striim/striim-ui";

interface Props {
    componentID: componentID;
    appName: string;
    templateID?: string;
    prefillWizardData?: Record<string, any>;
    onSave?: (data: Record<string, any>) => void;
    onCancel?: () => void;
}

const sanitize = (value: string) => value.replace(/[^A-Z0-9]+/gi, "_");

/**
 * The wizard names every source "<app>_Source", so a second source for a different adapter collides
 * with one already in the app — onRender's getSourceIfExists then loads that existing source (e.g. a
 * FileReader) instead of the adapter we asked for. Fetch the existing sources and derive an
 * adapter-specific name that doesn't clash with any of them (within the app's namespace).
 */
async function generateUniqueSourceName(componentID: string, appName: string): Promise<string> {
    const nsName = appName ? appName.split(".")[0] : "";
    const appPart = appName ? appName.split(".")[1] ?? appName : "";
    const base = `${sanitize(componentID)}_${sanitize(appPart)}_Source`;

    const existing = new Set<string>();
    try {
        const identifiers: any = await metaStoreService.fetchIdentifiersByType("SOURCE");
        identifiers?.forEach?.((identifier: any) => {
            if (!nsName || identifier.get("nsName") === nsName) {
                existing.add(String(identifier.get("name") || "").toLowerCase());
            }
        });
    } catch (e) {
        // If the lookup fails, fall back to the base name.
    }

    if (!existing.has(base.toLowerCase())) return base;
    let suffix = 1;
    while (existing.has(`${base}_${suffix}`.toLowerCase())) suffix++;
    return `${base}_${suffix}`;
}

const BaseSimpleSourceAI: React.FC<Props> = ({
    componentID,
    appName,
    templateID,
    prefillWizardData,
    onSave,
    onCancel
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<any>(null);

    useEffect(() => {
        if (!containerRef.current || !componentID) return;

        let cancelled = false;
        const view = new baseSimpleSourceModule.View({
            sourceAdapter: componentID,
            appName,
            skipFlowCreation: true,
            ...(templateID && { templateID }),
            ...(prefillWizardData && { prefillWizardData })
        });

        // Set a non-colliding name before render(), since onRender is where getSourceIfExists runs.
        (async () => {
            const name = await generateUniqueSourceName(componentID, appName);
            if (cancelled) return;
            view.model.set("name", name);

            view.render();
            if (cancelled || !containerRef.current) return;
            containerRef.current.replaceChildren(view.el);
            view.triggerMethod("show");
            viewRef.current = view;
        })();

        return () => {
            cancelled = true;
            viewRef.current = null;
            // Defer destroy to avoid unmounting React roots embedded in the Marionette view
            // while React is still in its commit phase.
            setTimeout(() => {
                view?.destroy();
            }, 0);
        };
    }, [componentID, appName, templateID]);

    const handleSave = async () => {
        const view = viewRef.current;
        if (!view) return;

        const errorFields: string[] = (await view.validateControls?.()) ?? [];
        if (errorFields.length > 0) {
            view.showError?.(
                "Fix the following errors in the source configuration<br/>Provide the missing inputs: " +
                    errorFields.map((field: string) => (getControlResource as any)(field)?.name ?? field).join(", ")
            );
            return;
        }
        view.showError?.(null);

        const model = view.model?.toJSON?.();
        onSave?.({
            name: model?.name,
            adapter: { name: model?.adapter?.handler, properties: model?.adapter?.properties },
            parser: { name: model?.parser?.handler, properties: model?.parser?.properties }
        });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
            <Box ref={containerRef} sx={{ flex: 1, backgroundColor: "#fff", p: 2, borderRadius: "8px" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", pt: 2 }}>
                <StriimButton variant="secondary" size="small" onClick={onCancel}>
                    Cancel
                </StriimButton>
                <StriimButton variant="primary" size="small" onClick={handleSave}>
                    Save
                </StriimButton>
            </Box>
        </Box>
    );
};

export default BaseSimpleSourceAI;
